import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Plus } from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import notificationService from "src/services/notification/notification.service";
import AdjustmentsTable from "../components/adjustments-table";
import AdjustmentDrawer from "../components/adjustment-drawer";
import type { CommissionAdjustment } from "../types/commissions.types";
import {
  useGetCommissionAdjustmentsQuery,
  useCreateCommissionAdjustmentMutation,
  useDeleteCommissionAdjustmentMutation,
} from "../endpoints/commissions.endpoints";

const AdjustmentsView: React.FC = () => {
  const dispatch = useDispatch();
  const { data: adjustments = [] } = useGetCommissionAdjustmentsQuery();
  const [createAdjustment] = useCreateCommissionAdjustmentMutation();
  const [deleteAdjustment, { isLoading: isDeleting }] =
    useDeleteCommissionAdjustmentMutation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleSaveAdjustment = async (payload: Omit<CommissionAdjustment, "id">) => {
    try {
      await createAdjustment(payload).unwrap();
      notificationService.handleSuccess(null, dispatch, "Adjustment created");
    } catch (error) {
      notificationService.handleError(error, dispatch, "Failed to create adjustment");
    }
  };

  const handleDeleteAdjustment = async (adjustment: CommissionAdjustment) => {
    const confirmed = window.confirm(
      `Remove adjustment for ${adjustment.targetName} (Q${adjustment.quarter} ${adjustment.year})?`
    );
    if (!confirmed) return;

    setDeletingId(adjustment.id);
    try {
      await deleteAdjustment({ id: adjustment.id }).unwrap();
      notificationService.handleSuccess(null, dispatch, "Adjustment removed");
    } catch (error) {
      notificationService.handleError(error, dispatch, "Failed to remove adjustment");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className={portalDs.bodySm}>
          Per-period manual adjustments for penalties, credits, and one-off corrections.
        </p>
        <PortalDsButton type="button" variant="secondary" size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" />
          Add adjustment
        </PortalDsButton>
      </div>
      <AdjustmentsTable
        adjustments={adjustments}
        onDelete={handleDeleteAdjustment}
        deletingId={isDeleting ? deletingId : null}
      />
      <AdjustmentDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSave={handleSaveAdjustment}
      />
    </div>
  );
};

export default AdjustmentsView;
